import { Table, Product, Category, Order, OrderItem, PaymentMethod } from '../types';
import * as indexedDBService from './indexedDBService';

// Generate unique ID
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Helper function to safely parse JSON
const safeParseJSON = <T>(jsonString: string | null, fallback: T): T => {
  if (!jsonString) return fallback;
  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return fallback;
  }
};

// Initialize storage with default data if needed
export const initializeStorage = async (): Promise<void> => {
  try {
    // Initialize IndexedDB first
    await indexedDBService.initDatabase();

    // Check if we need to initialize with default data
    const tables = await indexedDBService.getItems<Table>('tables');
    if (!tables || tables.length === 0) {
      console.log('No tables found, initializing with empty arrays');
      // Initialize with empty arrays
      try {
        await indexedDBService.saveItems<Table>('tables', []);
        await indexedDBService.saveItems<Product>('products', []);
        await indexedDBService.saveItems<Category>('categories', []);
        await indexedDBService.saveItems<Order>('orders', []);
        await indexedDBService.saveItems<PaymentMethod>('payment_methods', []);
      } catch (error) {
        console.error('Error initializing empty arrays:', error);
      }
    }
    
    // Try to migrate from localStorage if needed
    try {
      await indexedDBService.migrateFromLocalStorage();
    } catch (migrationError) {
      console.warn('Error during localStorage migration:', migrationError);
    }
  } catch (error) {
    console.error('Error initializing IndexedDB, falling back to localStorage:', error);
    
    // Fall back to localStorage if IndexedDB fails
    if (!localStorage.getItem('restaurant_tables')) {
      localStorage.setItem('restaurant_tables', JSON.stringify([]));
    }
    if (!localStorage.getItem('restaurant_products')) {
      localStorage.setItem('restaurant_products', JSON.stringify([]));
    }
    if (!localStorage.getItem('restaurant_categories')) {
      localStorage.setItem('restaurant_categories', JSON.stringify([]));
    }
    if (!localStorage.getItem('restaurant_orders')) {
      localStorage.setItem('restaurant_orders', JSON.stringify([]));
    }
    if (!localStorage.getItem('payment_methods')) {
      localStorage.setItem('payment_methods', JSON.stringify([]));
    }
  }
};

// Products
export const storeProducts = async (products: Product[]): Promise<void> => {
  try {
    console.log(`Attempting to store ${products.length} products in IndexedDB`);
    // Ensure all products have valid IDs
    const validProducts = products.map(product => {
      if (!product.id) {
        product.id = generateId();
      }
      return product;
    });
    
    await indexedDBService.saveItems<Product>('products', validProducts);
    console.log(`Successfully stored ${validProducts.length} products in IndexedDB`);
  } catch (error) {
    console.error('Error storing products in IndexedDB, falling back to localStorage:', error);
    const limitedProducts = products.slice(0, 50); // Limit to avoid quota issues
    localStorage.setItem('restaurant_products', JSON.stringify(limitedProducts));
  }
};

export const getProducts = async (): Promise<Product[]> => {
  try {
    const products = await indexedDBService.getItems<Product>('products');
    return products || [];
  } catch (error) {
    console.error('Error getting products from IndexedDB, falling back to localStorage:', error);
    return safeParseJSON(localStorage.getItem('restaurant_products'), []);
  }
};

export const getProductById = async (id: string): Promise<Product | undefined> => {
  try {
    const product = await indexedDBService.getItemById<Product>('products', id);
    return product;
  } catch (error) {
    console.error('Error getting product from IndexedDB, falling back to localStorage:', error);
    const products = safeParseJSON(localStorage.getItem('restaurant_products'), []);
    return products.find(product => product.id === id);
  }
};

// For compatibility with orderApiService
export const getProduct = async (id: string): Promise<Product | null> => {
  const product = await getProductById(id);
  return product || null;
};

// Categories
export const storeCategories = async (categories: Category[]): Promise<void> => {
  try {
    console.log(`Attempting to store ${categories.length} categories in IndexedDB`);
    // Ensure all categories have valid IDs
    const validCategories = categories.map(category => {
      if (!category.id) {
        category.id = generateId();
      }
      return category;
    });
    
    await indexedDBService.saveItems<Category>('categories', validCategories);
    console.log(`Successfully stored ${validCategories.length} categories in IndexedDB`);
  } catch (error) {
    console.error('Error storing categories in IndexedDB, falling back to localStorage:', error);
    // Only store a limited number of categories to avoid quota issues
    const limitedCategories = categories.slice(0, 20);
    localStorage.setItem('restaurant_categories', JSON.stringify(limitedCategories));
  }
};

export const getCategories = async (): Promise<Category[]> => {
  try {
    const categories = await indexedDBService.getItems<Category>('categories');
    return categories || [];
  } catch (error) {
    console.error('Error getting categories from IndexedDB, falling back to localStorage:', error);
    return safeParseJSON(localStorage.getItem('restaurant_categories'), []);
  }
};

export const getCategoryById = async (id: string): Promise<Category | undefined> => {
  try {
    const category = await indexedDBService.getItemById<Category>('categories', id);
    return category;
  } catch (error) {
    console.error('Error getting category from IndexedDB, falling back to localStorage:', error);
    const categories = safeParseJSON(localStorage.getItem('restaurant_categories'), []);
    return categories.find(category => category.id === id);
  }
};

// Tables
export const storeTables = async (tables: Table[]): Promise<void> => {
  try {
    await indexedDBService.saveItems<Table>('tables', tables);
  } catch (error) {
    console.error('Error storing tables in IndexedDB, falling back to localStorage:', error);
    localStorage.setItem('restaurant_tables', JSON.stringify(tables));
  }
};

export const getTables = async (): Promise<Table[]> => {
  try {
    const storedTables = await indexedDBService.getItems<Table>('tables');
    return (storedTables || []).map(table => ({
      ...table,
      status: table.status || 'available',
      orders: table.orders || []
    }));
  } catch (error) {
    console.error('Error getting tables from IndexedDB, falling back to localStorage:', error);
    const stored = localStorage.getItem('restaurant_tables');
    if (!stored) return [];

    try {
      const tables = JSON.parse(stored);
      return tables.map((table: any) => ({
        ...table,
        status: table.status || 'available',
        orders: table.orders || []
      }));
    } catch (parseError) {
      console.error('Error parsing tables from localStorage:', parseError);
      return [];
    }
  }
};

export const getTable = async (id: string): Promise<Table | undefined> => {
  try {
    const table = await indexedDBService.getItemById<Table>('tables', id);
    if (table) {
      return {
        ...table,
        status: table.status || 'available',
        orders: table.orders || []
      };
    }
    return undefined;
  } catch (error) {
    console.error('Error getting table from IndexedDB, falling back to localStorage:', error);
    const tables = await getTables();
    return tables.find(table => table.id === id);
  }
};

export const updateTableObservations = async (tableId: string, observations: string): Promise<void> => {
  try {
    const tables = await getTables();
    const tableIndex = tables.findIndex(t => t.id === tableId);

    if (tableIndex < 0) {
      throw new Error(`Mesa com ID ${tableId} não encontrada`);
    }

    tables[tableIndex].observations = observations;
    await storeTables(tables);
  } catch (error) {
    console.error('Erro ao atualizar observações da mesa:', error);
    throw error;
  }
};

export const createTable = (number: number, seats: number): Table => {
  const newTable: Table = {
    id: generateId(),
    number,
    seats,
    status: 'available',
    orders: [],
    createdAt: new Date() // Adicionando a data de criação
  };
  
  return newTable;
};

export const updateTable = async (updatedTable: Table): Promise<void> => {
  try {
    const tables = await getTables();
    const index = tables.findIndex(table => table.id === updatedTable.id);

    if (index >= 0) {
      tables[index] = updatedTable;
      await storeTables(tables);
    }
  } catch (error) {
    console.error('Erro ao atualizar mesa:', error);
    throw error;
  }
};

// Orders
export const getOrders = async (): Promise<Order[]> => {
  try {
    const orders = await indexedDBService.getItems<Order>('orders');
    return orders || [];
  } catch (error) {
    console.error('Error getting orders from IndexedDB, falling back to localStorage:', error);
    return safeParseJSON(localStorage.getItem('restaurant_orders'), []);
  }
};

export const getCompletedOrders = async (): Promise<Order[]> => {
  const orders = await getOrders();
  return orders.filter(order => order.status === 'paid');
};

export const getOrder = async (id: string): Promise<Order | undefined> => {
  try {
    const order = await indexedDBService.getItemById<Order>('orders', id);
    return order;
  } catch (error) {
    console.error('Error getting order from IndexedDB, falling back to localStorage:', error);
    const orders = await getOrders();
    return orders.find(order => order.id === id);
  }
};

export const createOrder = async (order: Order): Promise<void> => {
  try {
    const orders = await getOrders();
    orders.push(order);
    await indexedDBService.saveItems<Order>('orders', orders);

    // Also update the table to mark as occupied
    const table = await getTable(order.tableId);
    if (table) {
      table.status = 'occupied';
      table.timeOpened = new Date();
      table.orders = table.orders || [];
      table.orders.push(order);
      await updateTable(table);
    }
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const updateOrder = async (updatedOrder: Order): Promise<void> => {
  try {
    const orders = await getOrders();
    const index = orders.findIndex(order => order.id === updatedOrder.id);

    if (index >= 0) {
      // Make sure to preserve VendaFormaPagamento if it exists
      if (updatedOrder.VendaFormaPagamento && updatedOrder.VendaFormaPagamento.length > 0) {
        console.log('Saving order with payment methods:', updatedOrder.VendaFormaPagamento);
      }
      
      orders[index] = updatedOrder;
      await indexedDBService.saveItems<Order>('orders', orders);

      // Update the order in the table as well
      const table = await getTable(updatedOrder.tableId);
      if (table) {
        table.orders = table.orders || [];
        const tableOrderIndex = table.orders.findIndex(o => o.id === updatedOrder.id);
        if (tableOrderIndex >= 0) {
          table.orders[tableOrderIndex] = updatedOrder;
          await updateTable(table);
        }
      }
    }
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
};

export const updateOrderItemStatus = async (orderId: string, itemId: string, status: OrderItem['status']): Promise<void> => {
  try {
    const order = await getOrder(orderId);
    if (!order) return;

    const item = order.items.find(item => item.id === itemId);

    if (item) {
      item.status = status;
      await updateOrder(order);
    }
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
};

export const getKitchenOrders = async (): Promise<{ order: Order, items: OrderItem[] }[]> => {
  const orders = await getOrders();
  return orders.filter(o => o.status === 'open').map(order => {
    const items = order.items.filter(i =>
      i.status === 'pending' || i.status === 'preparing' || i.status === 'ready'
    );
    return { order, items };
  }).filter(({ items }) => items.length > 0);
};

export const getDeliveryOrders = async (): Promise<Order[]> => {
  const orders = await getOrders();
  return orders.filter(o =>
    o.type === 'delivery' && o.status === 'open'
  );
};

// Add this new function to store orders
export const storeOrders = async (orders: Order[]): Promise<void> => {
  try {
    await indexedDBService.saveItems<Order>('orders', orders);
    console.log(`Successfully stored ${orders.length} orders in IndexedDB`);
  } catch (error) {
    console.error('Error storing orders in IndexedDB, falling back to localStorage:', error);
    localStorage.setItem('restaurant_orders', JSON.stringify(orders));
  }
};

// PaymentMethods
export const storePaymentMethods = async (paymentMethods: PaymentMethod[]): Promise<void> => {
  try {
    console.log(`Attempting to store ${paymentMethods.length} payment methods in IndexedDB`);
    // Ensure all payment methods have valid IDs
    const validPaymentMethods = paymentMethods.map(method => {
      if (!method.id) {
        method.id = generateId();
      }
      return method;
    });
    
    await indexedDBService.saveItems<PaymentMethod>('payment_methods', validPaymentMethods);
    console.log(`Successfully stored ${validPaymentMethods.length} payment methods in IndexedDB`);
  } catch (error) {
    console.error('Error storing payment methods in IndexedDB, falling back to localStorage:', error);
    localStorage.setItem('payment_methods', JSON.stringify(paymentMethods));
  }
};

export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  try {
    const paymentMethods = await indexedDBService.getItems<PaymentMethod>('payment_methods');
    return paymentMethods || [];
  } catch (error) {
    console.error('Error getting payment methods from IndexedDB, falling back to localStorage:', error);
    return safeParseJSON(localStorage.getItem('payment_methods'), []);
  }
};

export const getPaymentMethodById = async (id: string): Promise<PaymentMethod | undefined> => {
  try {
    const paymentMethod = await indexedDBService.getItemById<PaymentMethod>('payment_methods', id);
    return paymentMethod;
  } catch (error) {
    console.error('Error getting payment method from IndexedDB, falling back to localStorage:', error);
    const paymentMethods = await getPaymentMethods();
    return paymentMethods.find(method => method.id === id);
  }
};

// Helper function to reset database and reinitialize
export const resetStorage = async (): Promise<void> => {
  try {
    await indexedDBService.resetDatabase();
    await initializeStorage();
    console.log('Storage reset and reinitialized successfully');
  } catch (error) {
    console.error('Error resetting storage:', error);
    throw error;
  }
};
